import { ChatBubbleLeftEllipsisIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid';
import { Alert, Badge, Button, Chip, Input, Option, Select, ThemeProvider, Typography } from '@material-tailwind/react';
import _ from 'lodash';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Refund, RefundSearchParams } from '../../types';
import { formatDateLong, formatDateShort, formatLabel } from '../lib/formatters';
import { formatMoney } from '../lib/money';
import useChatrooms from '../store/chatrooms';
import { useOrders } from '../store/orders';
import ChipSelector from './ChipSelector';
import ImageModal from './Modals/ImageModal';
import { Pagination } from './Pagination';
import RefundChip from './RefundChip';

const theme = {
	input: {
		styles: {
			base: {
				container: {
					minWidth: 'min-w-[50px]',
				},
			},
		},
	},
};

interface PaginationProps {
	activePage: number;
	setActivePage: (activePage: number) => void;
}

interface Props {
	refunds: Refund[];
	refundsLoaded: boolean;
	setRefundSearchParams: (value: RefundSearchParams) => void;
	refundSearchParams: RefundSearchParams | null;
	paginationProps: PaginationProps;
}

const RefundTable = ({ refunds, refundsLoaded, setRefundSearchParams, refundSearchParams, paginationProps }: Props) => {
	const {
		orderKey,
		date,
		disputeStatus,
		disputeStatusFilters,
		sortDirection,
	} = refundSearchParams || {};

	const { chatrooms } = useChatrooms();
	const [showImageModal, setShowImageModal] = useState(false);
	const [selectedImageLinks, setSelectedImageLinks] = useState([]);

	const debouncedSetOrderKeyQuery = _.debounce((orderKeyQuery) => {
		setRefundSearchParams({
			...refundSearchParams,
			orderKey: orderKeyQuery,
		});
	}, 500);

	// const debouncedSetProducerLocationKeyQuery = _.debounce((producerLocationKeyQuery) => { // Remove for now
	// 	setRefundSearchParams({
	// 		...refundSearchParams,
	// 		producerLocationKey: producerLocationKeyQuery,
	// 	});
	// }, 500);

	const toggleSortDirection = () => {
		setRefundSearchParams({
			...refundSearchParams,
			sortDirection: sortDirection === -1 ? 1 : -1,
		});
	};

	const headers = [
		{
			label: 'Order Id',
			fieldName: 'externalId',
			rowValueName: 'externalId',
			inputType: 'text',
			initialValue: orderKey,
			setter: debouncedSetOrderKeyQuery,
			width: 'w-[10%]',
		},
		{
			label: 'Order Date',
			fieldName: 'lateDelivery',
			rowValueName: 'date',
			inputType: 'date',
			initialValue: date,
			setter: (date) => {
				setRefundSearchParams({
					...refundSearchParams,
					date,
				});
			},
			width: 'w-[10%]',
		},
		{
			label: 'Location',
			fieldName: 'producerLocationName',
			rowValueName: 'producerLocationName',
			inputType: 'text',
		},
		{
			label: 'Dispute Reason',
			fieldName: 'disputeReason',
			rowValueName: 'disputeReason',
			inputType: 'text',
			initialValue: '',
			width: 'w-[10%]',
		},
		{
			label: 'Dispute Description',
			fieldName: 'disputeDescription',
			rowValueName: 'disputeDescription',
			inputType: 'text',
			initialValue: '',
		},
		{
			label: 'Review Description',
			fieldName: 'disputeDescription',
			rowValueName: 'disputeDescription',
			inputType: 'text',
			initialValue: '',
			colSpan: 2,
		},
		{
			label: 'Status',
			fieldName: 'disputeStatus',
			rowValueName: 'disputeStatus',
			initialValue: disputeStatus,
			setter: (disputeStatus) => {
				let newRefundSearchParams;
				if (disputeStatus === 'PENDING') {
					newRefundSearchParams = {
						...refundSearchParams,
						disputeStatus: null,
						disputeStatusFilters: 'DISPUTED,APPEALED',
					};
				} else if (disputeStatus === 'any') {
					newRefundSearchParams = {
						...refundSearchParams,
						disputeStatus: null,
						disputeStatusFilters: null,
					};
				} else {
					newRefundSearchParams = {
						...refundSearchParams,
						disputeStatusFilters: null,
						disputeStatus,
					};
				}
				setRefundSearchParams(newRefundSearchParams);
			},
			width: 'w-[15%]',
			customComponent: (setter) =>
				ChipSelector({
					setter,
					label: 'Status',
					value: disputeStatus || (disputeStatusFilters ? 'PENDING' : 'any'),
					options: [
						{ value: 'any', label: 'Any', color: 'gray', variant: 'ghost' },
						{ value: 'APPROVED', label: 'Approved', color: 'green' },
						{ value: 'PENDING', label: 'Pending', color: 'orange', variant: 'ghost' },
						{ value: 'DENIED', label: 'Denied', color: 'red', variant: 'ghost' },
					],
				}),
		},
	];

	return (
		<ThemeProvider value={theme}>
			<div className="flex justify-between items-center p-3 h-20">
				<h5 className="text-xl font-semibold">Refund Status</h5>
			</div>
			<div className="flex-col max-h-[calc(100vh-10rem)] items-center">
				<table className="border mt-4 w-full text-left">
					<TableHeaders
						headers={headers}
						toggleSortDirection={toggleSortDirection}
					/>
					<TableRows
						rows={refunds}
						refundsLoaded={refundsLoaded}
						headers={headers}
						chatrooms={chatrooms}
						setSelectedImageLinks={setSelectedImageLinks}
						setShowImageModal={setShowImageModal}
					/>
				</table>
				{paginationProps && (paginationProps.activePage !== 1 || refunds.length >= 10) && (
					<Pagination activePage={paginationProps.activePage} setActivePage={paginationProps.setActivePage} />
				)}
			</div>
			<ImageModal
				open={showImageModal}
				toggleOpen={() => setShowImageModal(!showImageModal)}
				title={'Related Images'}
				imageLinks={selectedImageLinks}
			/>
		</ThemeProvider>
	);
};

const StatusSelector = (setter) => {
	return (
		<div className="w-full gap-6 z-10">
			<Select size="md" label="Status" onChange={setter}>
				<Option value={undefined}>
					<Chip value={`Any`} color="gray" variant="ghost" size="sm" />
				</Option>
				<Option value={'APPROVED'}>
					<Chip
						value={`Approved`}
						color="green"
						// variant="ghost"
						size="sm"
					/>
				</Option>
				<Option value={'PENDING'}>
					<Chip value={`Pending`} color="orange" variant="ghost" size="sm" className="w-full" />
				</Option>
				<Option value={'DENIED'}>
					<Chip value={`Denied`} color="red" variant="ghost" size="sm" />
				</Option>
			</Select>
		</div>
	);
};

const TableHeaders = ({
	headers,
	toggleSortDirection,
}) => {
	const [headerValues, setHeaderValues] = useState(headers.map((h) => h.initialValue));

	return (
		<thead>
			<tr>
				{headers.map((head, index) => (
					<th
						key={head.label}
						className={`cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-4 transition-colors hover:bg-blue-gray-50 ${
							head.width || ''
						}`}
                        colSpan={head.colSpan || 1}
					>
						<div className="flex items-center justify-between gap-2 font-normal leading-none">
							{!head.customComponent ? (
								<div className="w-full gap-6 opacity-70">
									{head.setter ? (
										<Input
											label={head.label}
											crossOrigin=""
											variant="standard"
											size="md"
											value={headerValues[index]}
											placeholder={head.placeholder}
											type={head.inputType}
											onChange={(event) => {
												setHeaderValues({
													...headerValues,
													[index]: event.target.value,
												});
												head.setter(event.target.value);
											}}
										/>
									) : (
										<Typography variant="small" color="blue-gray" className="font-[420]">
											{head.label}
										</Typography>
									)}
								</div>
							) : (
								head.customComponent(head.setter)
							)}
							{head.fieldName === 'lateDelivery' && (
								<ChevronUpDownIcon strokeWidth={2} className="h-4 w-4" onClick={toggleSortDirection} />
							)}
						</div>
					</th>
				))}
			</tr>
		</thead>
	);
};

const TableRows = ({ rows, refundsLoaded, headers, chatrooms, setSelectedImageLinks, setShowImageModal }) => {
	const navigate = useNavigate();
	const { orders: activeOrders } = useOrders.getState();

	return (
		<tbody>
			{!refundsLoaded && rows.length === 0 && (
				<tr className="border-b-0">
					<td colSpan={headers.map(h => h.colSpan || 1).reduce((a, b) => a+b)} className="p-8 py-4">
						<Alert color="orange" variant="ghost">
							Loading...
						</Alert>
					</td>
				</tr>
			)}
			{rows.map((refundDispute, index) => {
				const {
					orderKey,
					externalId,
					// producerKey, // Remove for now
					producerLocationName,
					// orderSourceKey,
					disputeReason,
					disputeDescription,
					reviewDescription,
					lateDelivery,
					imageLinks,
					invoiceUrl,
					invoiceId,
					orderRefund,
				} = refundDispute;
				const isLast = index === rows.length - 1;
				const classes = isLast ? 'p-4' : 'p-4 border-b border-blue-gray-50';

				const chatroomHasNewMessage = chatrooms[orderKey] && !chatrooms[orderKey].hasBeenReadByParticipant;

                const hasAssociatedImages = imageLinks?.length > 0;
                const hasAssociatedInvoiceWithRefund = orderRefund !== 0 && invoiceUrl;

				return (
					<tr
						key={orderKey + `${index}`}
						className={
							refundsLoaded ? 'cursor-pointer hover:bg-gray-100' : 'pointer-events-none bg-gray-100'
						}
						onClick={() =>
							navigate(`/${activeOrders[orderKey] ? 'active-orders' : 'order-history'}/${orderKey}`)
						}
					>
						<td className={classes}>
							<div className="flex items-start">
								<Badge
									content={<ChatBubbleLeftEllipsisIcon />}
									invisible={!chatroomHasNewMessage}
									placement="top-end"
								>
									<div className={`flex flex-col gap-y-1 pr-5`}>
										<div className={`flex items-center`}>
											<Typography
												variant="small"
												color="blue-gray"
												className="font-semibold break-words"
											>
												{externalId}
											</Typography>
										</div>
										<Typography
											variant="small"
											color="blue-gray"
											className="font-normal opacity-70"
										>
											{orderKey}
										</Typography>
									</div>
								</Badge>
							</div>
						</td>
						<td className={classes}>
							<Typography variant="small" color="blue-gray" className="font-normal">
								{formatDateLong(lateDelivery)}
							</Typography>
						</td>
						<td className={classes}>
							<div className="flex flex-col">
								<Typography variant="small" color="blue-gray" className="font-semibold">
									{producerLocationName}
								</Typography>
							</div>
						</td>
						<td className={classes}>
							<div className="flex flex-col">
								<Typography variant="small" color="blue-gray" className="font-semibold">
									{formatLabel(disputeReason)}
								</Typography>
							</div>
						</td>
						<td className={classes}>
							<div className="flex flex-col">
								<Typography variant="small" color="blue-gray" className="font-semibold">
									{disputeDescription}
								</Typography>
							</div>
						</td>
                        <td colSpan={(hasAssociatedImages || hasAssociatedInvoiceWithRefund) ? 1 : 2} className={classes}>
							<div className="flex flex-col">
								<Typography variant="small" color="blue-gray" className="font-semibold">
									{reviewDescription}
								</Typography>
							</div>
						</td>
						{(hasAssociatedImages || hasAssociatedInvoiceWithRefund) && <td className={classes}>
							<div className="flex flex-col gap-y-2">
								{hasAssociatedImages && (
									<Button
										variant="gradient"
										size="sm"
										color="indigo"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											setSelectedImageLinks(imageLinks);
											setShowImageModal(true);
										}}
									>
										Images
									</Button>
								)}
								{hasAssociatedInvoiceWithRefund && (
									<Button
										variant="gradient"
										size="sm"
										color="purple"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											window.open(invoiceUrl[0]);
										}}
									>
										{`Invoice #${invoiceId[0]}`}
									</Button>
								)}
							</div>
						</td>}
                        <td className={classes}>
							<div className="flex flex-col gap-1">
								<div className="flex w-full justify-left items-center gap-x-2">
									<RefundStatusBadge refundDispute={refundDispute} />
								</div>
								{/* <Typography // Remove for now
										variant="small"
										color="blue-gray"
										className="font-normal italic w-full text-left opacity-70"
									>
										{`at ${formatDateTimeShort(updatedAt)}`}
									</Typography> */}
							</div>
						</td>
					</tr>
				);
			})}
		</tbody>
	);
};

const RefundStatusBadge = ({ refundDispute }) => {
	const {
		disputeStatus,
		finalRefundAmount,
		orderRefund,
		invoiceUrl,
		invoiceCorrectionId,
		correctionChargedTimestamp,
	} = refundDispute;

	switch (disputeStatus) {
		case 'APPROVED':
			const refundedOnInvoice = orderRefund && invoiceUrl;
			const refundedOnCompletedCorrection = invoiceCorrectionId && correctionChargedTimestamp;
			const invoiceCorrectionPending = invoiceCorrectionId && !correctionChargedTimestamp;
			const invoiceWithRefundPending = orderRefund && !invoiceUrl;
			const refundHasBeenCharged = refundedOnInvoice || refundedOnCompletedCorrection;
			return (
				<RefundChip
					value={`Refunded for ${formatMoney(Math.abs(finalRefundAmount))}`}
					color="green"
					tooltipHeader={refundHasBeenCharged ? `Chargeback Completed` : `Chargeback Pending`}
					tooltipContent={
						!refundHasBeenCharged
							? invoiceCorrectionPending
								? `Will be reflected in upcoming charge to your account and on a charge summary email to be received on the same date`
								: invoiceWithRefundPending && `Refund will be reflected on the next invoice`
							: refundedOnInvoice
							? `Reflected on Invoice`
							: `Reflected in total amount charged on ${formatDateShort(correctionChargedTimestamp)}}`
					}
				/>
			);
		case 'DENIED':
			return <RefundChip value={`Refund Denied`} color="red" variant="ghost" />;
		case 'APPEALED':
		case 'DISPUTED':
			return <RefundChip value={`Refund Pending`} color="orange" variant="ghost" />;
        default:
            return <></>;
	}
};

export default RefundTable;
